/**
 * CelebrationFX - Dynamic Particle Confetti, Screen Pulse & Celebration Fanfare
 * Features lightweight HTML5 Canvas physics particles (fluttering metallic ribbons,
 * gravity, aerodynamic drag, 3D spin) and screen vignette pulse flashes.
 */

export class CelebrationFX {
  /**
   * Triggers a screen radial vignette pulse flash.
   * @param {'gold'|'skunk'|'royal'|'legendary'} [type='gold']
   * @param {number} [duration=1200] Duration in milliseconds
   */
  static pulseScreen(type = 'gold', duration = 1200) {
    if (typeof document === 'undefined') return;
    let overlay = document.getElementById('celebrationPulseOverlay');
    if (!overlay) {
      overlay = document.createElement('div');
      overlay.id = 'celebrationPulseOverlay';
      overlay.style.cssText = `
        position: fixed;
        inset: 0;
        pointer-events: none;
        z-index: 9998;
        opacity: 0;
        transition: opacity 0.25s ease-out;
      `;
      document.body.appendChild(overlay);
    }

    let gradient = 'radial-gradient(circle at center, rgba(255, 215, 0, 0.28) 0%, rgba(212, 160, 23, 0.45) 60%, rgba(122, 85, 0, 0.6) 100%)';
    if (type === 'skunk') {
      gradient = 'radial-gradient(circle at center, rgba(148, 163, 184, 0.25) 0%, rgba(71, 85, 105, 0.45) 60%, rgba(15, 23, 42, 0.75) 100%)';
    } else if (type === 'royal' || type === 'legendary') {
      gradient = 'radial-gradient(circle at center, rgba(254, 240, 138, 0.35) 0%, rgba(234, 179, 8, 0.45) 45%, rgba(168, 85, 247, 0.4) 75%, rgba(88, 28, 135, 0.65) 100%)';
    }

    overlay.style.background = gradient;
    overlay.style.opacity = '1';

    setTimeout(() => {
      if (overlay) {
        overlay.style.transition = `opacity ${Math.max(0.3, (duration - 300) / 1000)}s ease-out`;
        overlay.style.opacity = '0';
      }
    }, 280);
  }

  /**
   * Launches dynamic particle confetti cannons.
   * @param {Object} [options={}]
   * @param {number} [options.count=80] Number of confetti pieces
   * @param {Array<string>} [options.colors] Array of hex/rgb color strings
   * @param {number} [options.duration=3200] Lifespan in ms
   * @param {boolean} [options.sound=true] Whether to trigger audio pop
   * @param {AudioManager} [options.sfx] Optional AudioManager instance
   */
  static launchConfetti(options = {}) {
    if (typeof window === 'undefined' || typeof document === 'undefined') return;

    const count = options.count || 85;
    const colors = options.colors || [
      '#ffd700', // Metallic Gold
      '#f59e0b', // Amber
      '#ef4444', // Ruby Red
      '#3b82f6', // Sapphire Blue
      '#10b981', // Emerald Green
      '#ec4899', // Crimson Pink
      '#a855f7', // Regal Purple
      '#f8fafc'  // Sterling Silver
    ];
    const duration = options.duration || 3400;

    // Optional audio pop
    if (options.sound !== false) {
      const sfx = options.sfx || window.sfx;
      if (sfx && typeof sfx.confettiPop === 'function') {
        sfx.confettiPop();
      }
    }

    // Canvas setup
    const canvas = document.createElement('canvas');
    canvas.id = 'celebrationConfettiCanvas';
    canvas.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100vw;
      height: 100vh;
      pointer-events: none;
      z-index: 9999;
    `;
    document.body.appendChild(canvas);

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      canvas.remove();
      return;
    }

    const dpr = window.devicePixelRatio || 1;
    let width = (canvas.width = window.innerWidth * dpr);
    let height = (canvas.height = window.innerHeight * dpr);

    const particles = [];
    for (let i = 0; i < count; i++) {
      // Half from left cannon, half from right cannon
      const fromLeft = i % 2 === 0;
      const startX = fromLeft ? width * 0.15 : width * 0.85;
      const startY = height * 0.85;
      const angle = fromLeft
        ? -Math.PI * 0.25 - Math.random() * 0.45
        : -Math.PI * 0.75 + Math.random() * 0.45;
      const speed = (width * 0.012 + Math.random() * (width * 0.018)) * (0.8 + Math.random() * 0.5);

      particles.push({
        x: startX,
        y: startY,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        w: (8 + Math.random() * 10) * dpr,
        h: (4 + Math.random() * 6) * dpr,
        color: colors[Math.floor(Math.random() * colors.length)],
        rotation: Math.random() * Math.PI * 2,
        rSpeed: (Math.random() * 0.2 - 0.1),
        tilt: Math.random() * Math.PI,
        tSpeed: 0.08 + Math.random() * 0.12,
        gravity: 0.32 * dpr,
        drag: 0.982,
        opacity: 1.0
      });
    }

    let animId = null;
    const startTime = performance.now();

    function render(now) {
      const elapsed = now - startTime;
      const progress = elapsed / duration;

      if (progress >= 1) {
        if (animId) cancelAnimationFrame(animId);
        canvas.remove();
        return;
      }

      ctx.clearRect(0, 0, width, height);

      particles.forEach(p => {
        p.vx *= p.drag;
        p.vy = p.vy * p.drag + p.gravity;
        p.x += p.vx;
        p.y += p.vy;
        p.rotation += p.rSpeed;
        p.tilt += p.tSpeed;

        if (progress > 0.7) {
          p.opacity = Math.max(0, 1 - (progress - 0.7) / 0.3);
        }

        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rotation);
        ctx.scale(1, Math.sin(p.tilt)); // 3D ribbon flutter
        ctx.globalAlpha = p.opacity;
        ctx.fillStyle = p.color;
        ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
        ctx.restore();
      });

      animId = requestAnimationFrame(render);
    }

    animId = requestAnimationFrame(render);
  }

  /**
   * Triggers grand celebration fanfare and FX on high-scoring events.
   * @param {number} score
   * @param {Object} [deps={}]
   */
  static triggerCelebration(score, deps = {}) {
    const sfx = deps.sfx || (typeof window !== 'undefined' ? window.sfx : null);

    if (score === 29) {
      CelebrationFX.pulseScreen('legendary', 2400);
      CelebrationFX.launchConfetti({
        count: 140,
        colors: ['#ffd700', '#f59e0b', '#ef4444', '#3b82f6', '#10b981', '#ffffff', '#a855f7'],
        duration: 4200,
        sfx
      });
      if (sfx && typeof sfx.fanfare29 === 'function') {
        sfx.fanfare29();
      }
    } else if (score >= 20) {
      CelebrationFX.pulseScreen('gold', 1600);
      CelebrationFX.launchConfetti({
        count: 85,
        colors: ['#ffd700', '#f59e0b', '#ef4444', '#3b82f6', '#ffffff'],
        duration: 3200,
        sfx
      });
      if (sfx && typeof sfx.fanfare20 === 'function') {
        sfx.fanfare20();
      }
    }
  }
}
